import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router";
import { toast } from "sonner";
import { Spinner, Button, Box, Card } from "components/ui";
import { CheckCircleIcon, XCircleIcon, ArrowLeftIcon } from "@heroicons/react/24/outline";
import { exchangeFacebookCodeForToken } from "services/integrationService";

const FacebookCallback = () => {
  const [searchParams] = useSearchParams();
  const fullUrl = window.location.href;
const completeCode = fullUrl.split("code=")[1];
const providerCode = localStorage.getItem("integrationProviderCode");
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null); // 'success' | 'error'
  const [responseData, setResponseData] = useState(null);

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get("code");
        const errorCode = searchParams.get("error_code");
        const errorMessage = searchParams.get("error_message");
        // Check if it's an error callback
        if (errorCode || errorMessage) {
          const decodedErrorMessage = errorMessage ? decodeURIComponent(errorMessage) : "Facebook authentication failed";
          setStatus("error");
          setResponseData({
            error_code: errorCode,
            error_message: decodedErrorMessage
          });
          toast.error("Facebook authentication failed");
          setLoading(false);
          return;
        }

        // Check if we have the authorization code
        if (!code) {
          setStatus("error");
          setResponseData({
            error: "No authorization code received from Facebook"
          });
          toast.error("Facebook authentication failed - no authorization code");
          setLoading(false);
          return;
        }

        const providerId = localStorage.getItem("integrationProviderId");

        if (!providerId) {
          setStatus("error");
          setResponseData({
            error: "Missing provider information. Please restart the integration process.",
          });
          toast.error("Missing provider information. Please restart the integration process.");
          setLoading(false);
          return;
        }

        const result = await exchangeFacebookCodeForToken(completeCode, providerId);

        if (result.success) {
          toast.success(
            result.message || "Facebook integration completed successfully!",
          );
          localStorage.removeItem("integrationProviderId");
          navigate("/settings/integrations");
          return;
        }

        setStatus("error");
        setResponseData({
          error: result.error || result.message || "Failed to complete Facebook integration",
        });
        toast.error(result.error || result.message || "Failed to complete Facebook integration");
      } catch (error) {
        console.error("Facebook callback error:", error);
        setStatus("error");
        setResponseData({
          error: "An unexpected error occurred while processing Facebook authentication"
        });
        toast.error("An unexpected error occurred");
      } finally {
        setLoading(false);
      }
    };

    handleCallback();
  }, [navigate, searchParams]);

  const handleGoBack = () => {
    navigate("/settings/integrations");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
        <Card className="p-8 max-w-md w-full mx-4">
          <div className="text-center">
            <Spinner size="lg" className="mx-auto mb-4" />
            <h2 className="text-lg font-semibold text-gray-900 dark:text-dark-100 mb-2">
              Processing {providerCode} Integration
            </h2>
            <p className="text-sm text-gray-600 dark:text-dark-400">
              Please wait while we complete your {providerCode} authentication...
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-dark-900">
      <Card className="p-8 max-w-lg w-full mx-4">
        <div className="text-center">
          {status === "success" ? (
            <>
              <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900 mb-4">
                <CheckCircleIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-100 mb-2">
                {providerCode} Integration Successful!
              </h2>
              <p className="text-sm text-gray-600 dark:text-dark-400 mb-6">
                {responseData?.message || `Your ${providerCode} account has been successfully connected to Sensation CRM.`}
              </p>
              {responseData?.data && (
                <Box className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4 mb-6 text-left">
                  <h3 className="text-sm font-medium text-green-800 dark:text-green-200 mb-2">
                    Integration Details:
                  </h3>
                  <p className="text-xs text-green-700 dark:text-green-300">
                    {responseData.data}
                  </p>
                </Box>
              )}
            </>
          ) : (
            <>
              <div className="mx-auto flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900 mb-4">
                <XCircleIcon className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-dark-100 mb-2">
                {providerCode} Integration Failed
              </h2>
              <p className="text-sm text-gray-600 dark:text-dark-400 mb-6">
                We encountered an issue while connecting your {providerCode} account.
              </p>
              {responseData && (
                <Box className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 mb-6 text-left">
                  <h3 className="text-sm font-medium text-red-800 dark:text-red-200 mb-2">
                    Error Details:
                  </h3>
                  {responseData.error_code && (
                    <p className="text-xs text-red-700 dark:text-red-300 mb-1">
                      <strong>Error Code:</strong> {responseData.error_code}
                    </p>
                  )}
                  <p className="text-xs text-red-700 dark:text-red-300">
                    <strong>Message:</strong> {responseData.error_message || responseData.error || responseData.message || "Unknown error occurred"}
                  </p>
                </Box>
              )}
            </>
          )}

          <Button
            onClick={handleGoBack}
            variant="outlined"
            className="w-full"
          >
            <ArrowLeftIcon className="w-4 h-4 mr-2" />
            Back to Integrations
          </Button>
        </div>
      </Card>
    </div>
  );
};

export default FacebookCallback;
